/**
 * The left nav — and, at the top of the file, the router.
 *
 * WHY THE ROUTER LIVES HERE. This app has five screens and no server actions, no
 * SSR and no data loaders, so it needs about thirty lines of history API and can
 * therefore have none of react-router's client-side advisories by not having any
 * of its features. Those thirty lines want exactly one owner, and the nav is both
 * their primary consumer and a leaf module — it imports nothing from the app —
 * so putting them here keeps the module graph acyclic. App.tsx imports
 * `useRoute` from the file that defines the links it routes between, which is
 * the right way round: the nav is the enumeration of what exists. `useLinkToken`
 * is here for the narrower version of the same rule: it writes to `history`, and
 * every write to `history` in this app is in this file.
 *
 * THE NAV IS FOUR GROUPS AND THAT IS DELIBERATE. Six screens do not need eight
 * headings. What separates them is not subject matter but WHO the screen is for:
 * `Calls` is the record of what happened, read after the fact by whoever is
 * asking whether it was us or them; `Configuration` is the small set of things a
 * human sets before a call happens; `Access` is for whoever decides which humans
 * get to do either, which is a third audience and not a subheading of the second
 * — the agent form changes what a call does, and the users screen changes who is
 * allowed near a recording of one. Sessions is first because it is the landing
 * screen and the product.
 *
 * `Playground` IS THE FOURTH AUDIENCE AND NOT A SUBHEADING OF `Configuration`. An
 * agent's voice and prompt are settings that outlive the person setting them; the
 * playground is a workbench where somebody tries a widget against a real agent,
 * decides how it should look on a stranger's website, and leaves with a line of
 * HTML. Filing it under Configuration would put a page you POKE AT next to pages
 * you SAVE, and the two are read in different frames of mind.
 *
 * "Start a call" IS IN THE NAV rather than being only a button on the sessions
 * screen, because it is how the demo begins and the demo begins from wherever the
 * presenter happens to be. It carries no badge: it is a door, not a queue.
 *
 * A NAV ITEM A ROLE CANNOT USE IS NOT RENDERED. Starting a call is a POST, so for
 * a viewer that door leads to a 403 — and a nav item is a promise that the screen
 * behind it works. `Users`, by contrast, is shown to everybody, because the
 * contract lets any role read the list and the screen itself drops its controls.
 * Hiding a readable screen would be hiding something the server hands over
 * anyway.
 *
 * The badges are counts of things, not of work outstanding, with one exception —
 * a live call. A call in progress is the one number on this screen that changes
 * while you look at it, so it gets the pulsing dot rather than a number in a
 * box.
 *
 * THE IDENTITY BLOCK IS IN THE FOOTER AND TAKES ITS USER AS A PROP. This file is
 * a leaf module on purpose — see the note above about the router living here — so
 * it reads no context of its own. The shell already passes it counts and a path;
 * the signed-in user and the sign-out callback arrive the same way.
 */
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import {
  BotIcon,
  ChevronsUpDownIcon,
  ForwardIcon,
  ListIcon,
  LogOutIcon,
  MicIcon,
  PhoneCallIcon,
  PhoneIcon,
  SquareMousePointerIcon,
  UserIcon,
  UsersIcon,
  WebhookIcon,
  WrenchIcon,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { can, type Capability, type PanelUser } from '@/lib/auth'
import { ThemeSubMenu } from '@/components/theme-toggle'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '@/components/ui/sidebar'

// ===========================================================================
// Routing. Thirty lines instead of a dependency.
// ===========================================================================

/**
 * The current path and query, in state so that `pushState` re-renders.
 *
 * `popstate` alone is not enough: the browser fires it for back and forward but
 * never for a programmatic push, so `navigate` dispatches a synthetic one. That
 * is the whole trick, and it is why there is only one subscription here.
 */
export function useRoute(): string {
  const [route, setRoute] = useState(() => location.pathname + location.search)
  useEffect(() => {
    const on = () => setRoute(location.pathname + location.search)
    addEventListener('popstate', on)
    return () => removeEventListener('popstate', on)
  }, [])
  return route
}

/** Push a path without a reload, and tell the app to re-render. */
export function navigate(to: string): void {
  if (to === location.pathname + location.search) return
  history.pushState({}, '', to)
  dispatchEvent(new PopStateEvent('popstate'))
  // A new screen starts at the top. A router that preserves the old scroll
  // position lands the reader in the middle of a session they just opened.
  scrollTo(0, 0)
}

/**
 * A credential that arrived in a link: held once, then scrubbed from the address
 * bar.
 *
 * RESET AND INVITE TOKENS ARE LIVE, SINGLE-USE CREDENTIALS, and `?token=` leaves
 * one in three places it has no business being. The browser's own history, where
 * it outlives the tab and the person. `document.referrer`, which hands it to
 * whoever is on the far end of any outbound link the page renders. And
 * `location.search`, where any injected script can read it off the URL rather
 * than having to reach into React. None of the three is hypothetical on a page
 * whose entire purpose is to be arrived at from an email client.
 *
 * THE VALUE IS CAPTURED BEFORE THE URL IS TOUCHED, and that ordering is the whole
 * hook. The two screens take their token as a prop that App derives from
 * `useRoute()`, so emptying the search string makes that prop go null — and a
 * form that vanished halfway through somebody choosing a password would be a
 * worse bug than the one being fixed. The captured value is what everything reads
 * afterwards; the prop is a seed and nothing more, including for the "this link
 * has no token" branch.
 *
 * `replaceState`, NOT `pushState`: an address with a live token in it must not be
 * somewhere the back button can return to. And no synthetic `popstate` either —
 * `useRoute` is deriving the seed above from the very string being emptied, and
 * announcing the change would tell every screen to re-read a URL we have just
 * stripped.
 */
export function useLinkToken(seed: string | null): string | null {
  // The initialiser runs on the first render only, which is the one render where
  // the prop is still the real thing.
  const [token] = useState(() => seed)
  useEffect(() => {
    if (token === null) return
    history.replaceState({}, '', location.pathname)
  }, [token])
  return token
}

/**
 * An internal link. A REAL `<a href>`, so middle-click, ctrl-click, "open in new
 * tab" and the browser's own status bar all work — which is the half of routing
 * that hand-rolled implementations usually drop. Anything that is not a plain
 * left click is handed straight back to the browser.
 */
export function Link({
  to,
  children,
  ...rest
}: { to: string; children: ReactNode } & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a
      href={to}
      {...rest}
      onClick={(e) => {
        if (
          e.defaultPrevented
          || e.button !== 0
          || e.metaKey
          || e.ctrlKey
          || e.shiftKey
          || e.altKey
        ) {
          return
        }
        e.preventDefault()
        navigate(to)
      }}
    >
      {children}
    </a>
  )
}

// ===========================================================================
// The nav itself
// ===========================================================================

export interface NavCounts {
  /** Sessions in the whole history, not on the current page. */
  sessions?: number
  /** Sessions in a LIVE or LIVE_DEGRADED_* state right now. */
  live?: number
  agents?: number
}

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  count?: keyof NavCounts
  /** Omitted when the signed-in role lacks this. See the file header. */
  needs?: Capability
}

const GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: 'Calls',
    items: [
      { to: '/', label: 'Sessions', icon: ListIcon, count: 'sessions' },
      { to: '/call', label: 'Start a call', icon: PhoneCallIcon, needs: 'write' },
    ],
  },
  {
    label: 'Configuration',
    // Tools and webhooks sit beside Agents because they are the same kind of
    // thing: settings a human saves before a call happens. Both carry no
    // `needs`, for the Users reason rather than the Start-a-call reason — any
    // role may GET the lists, and the screens drop their own write controls.
    // No counts either: these registries hold a handful of rows, and a badge
    // saying "3" next to a heading is noise, not news.
    //
    // Registering an item here is HALF of adding a screen — the route lives in
    // App.tsx (see this file's header for why the router and the nav are
    // separate owners), and `crumbFor` below walks these GROUPS, so exact-path
    // breadcrumbs come free with this entry.
    items: [
      { to: '/agents', label: 'Agents', icon: BotIcon, count: 'agents' },
      { to: '/tools', label: 'Tools', icon: WrenchIcon },
      { to: '/webhooks', label: 'Webhooks', icon: WebhookIcon },
      // The desks a caller can be handed to. Beside Tools and Webhooks because it
      // is the same kind of thing — a library an agent points at — and above
      // Telephony because a target is agent configuration, not carrier
      // inventory. ForwardIcon rather than a phone glyph: three phones in one
      // group would stop distinguishing anything.
      { to: '/transfers', label: 'Transfers', icon: ForwardIcon },
      // PhoneIcon, not PhoneCallIcon — that one is the Start-a-call door above,
      // and two nav rows with the same glyph would read as the same place.
      { to: '/telephony', label: 'Telephony', icon: PhoneIcon },
    ],
  },
  {
    label: 'Playground',
    // No `needs`, and it is the same reasoning as `Users` rather than the same
    // reasoning as `Start a call`: the screen behind this door renders for every
    // role and drops its own write controls, so hiding it from a viewer would hide
    // four working previews they are allowed to look at. No badge — the number of
    // widgets is fixed at four and would be noise.
    items: [
      { to: '/playground/buttons', label: 'Voice Widgets', icon: SquareMousePointerIcon },
    ],
  },
  {
    label: 'Access',
    // No `needs`: every role may read the list, and the screen itself decides
    // which controls to draw. See the file header.
    items: [{ to: '/users', label: 'Users', icon: UsersIcon }],
  },
]

/** 1,227 is unreadable in a 3rem rail. 1.2k is not. */
function short(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}k` : String(n)
}

export function AppSidebar({
  path,
  counts,
  user,
  onSignOut,
}: {
  path: string
  counts: NavCounts
  user: PanelUser | null
  onSignOut: () => void
}) {
  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/">
                <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                  <MicIcon className="size-4" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">Voice AI</span>
                  <span className="truncate text-xs text-muted-foreground">
                    Control panel
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {GROUPS.map((group) => {
          const items = group.items.filter((i) => !i.needs || can(user, i.needs))
          // A group whose every item was filtered out is a heading over nothing.
          if (items.length === 0) return null
          return (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {items.map((item) => {
                  const n = item.count ? counts[item.count] : undefined
                  // A live call outranks the total. On the sessions row the badge
                  // becomes the pulsing dot while anything is up, because "one
                  // call is happening right now" is a more useful fact than "you
                  // have 41 of them on file".
                  const showLive = item.to === '/' && (counts.live ?? 0) > 0
                  return (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton
                        asChild
                        // Exact match, plus the two detail routes keeping their
                        // parent lit: a reader two clicks deep should still be
                        // able to see where they are.
                        isActive={
                          path === item.to
                          || (item.to === '/' && path.startsWith('/sessions/'))
                          || (item.to === '/agents' && path.startsWith('/agents/'))
                          || (item.to === '/tools' && path.startsWith('/tools/'))
                          || (item.to === '/webhooks' && path.startsWith('/webhooks/'))
                        }
                        tooltip={
                          showLive
                            ? `${item.label} — ${counts.live} live now`
                            : n === undefined
                              ? item.label
                              : `${item.label} — ${n.toLocaleString('en-GB')}`
                        }
                      >
                        <Link to={item.to}>
                          <item.icon />
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                      {showLive ? (
                        <SidebarMenuBadge>
                          <span className="live-dot" title={`${counts.live} live now`} />
                        </SidebarMenuBadge>
                      ) : (
                        n !== undefined
                        && n > 0 && (
                          <SidebarMenuBadge>
                            <span className="text-muted-foreground">{short(n)}</span>
                          </SidebarMenuBadge>
                        )
                      )}
                    </SidebarMenuItem>
                  )
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          )
        })}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          {/*
            The timings caveat ("no OpenAI realtime event carries a wall clock;
            every latency is measured at our Durable Object") used to live here
            as a footnote item. Removed on request — it is stated in full on the
            session page, where the numbers are. The theme picker moved into the
            user menu below for the same tidiness.
          */}
          {/*
            LAST, AND THEREFORE BOTTOM-MOST. It is where a signed-in person looks
            for their own name, and where they look for the way out. Rendered only
            when there is a user because the shell is only mounted when there is
            one — but the guard stays, because a nav that throws while somebody is
            being signed out is a nav that hides the sign-out button.
          */}
          {user && (
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton size="lg" tooltip={`${user.email} · ${user.role}`}>
                    <div className="bg-sidebar-accent text-sidebar-accent-foreground flex aspect-square size-8 items-center justify-center rounded-lg text-xs font-semibold">
                      {(user.name ?? user.email).trim().slice(0, 1).toUpperCase()}
                    </div>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      {/* The name if there is one, the address if not. Never a
                          placeholder: "Unnamed user" tells a reader nothing about
                          which account they are signed in as. */}
                      <span className="truncate font-medium">{user.name ?? user.email}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {user.role}
                      </span>
                    </div>
                    <ChevronsUpDownIcon className="ml-auto size-4" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                {/* Opens UPWARD. The trigger is the last thing in the sidebar, so
                    a menu below it would be off the bottom of the window. */}
                <DropdownMenuContent side="top" align="start" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <span className="block truncate text-sm font-medium">
                      {user.name ?? 'Signed in'}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {user.email}
                    </span>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/account">
                      <UserIcon />
                      Account
                    </Link>
                  </DropdownMenuItem>
                  <ThemeSubMenu />
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onSignOut}>
                    <LogOutIcon />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          )}
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}

/** Breadcrumb text for a path — group above, screen below. */
export function crumbFor(path: string): { group: string; title: string } {
  for (const g of GROUPS) {
    for (const i of g.items) if (i.to === path) return { group: g.label, title: i.label }
  }
  if (path.startsWith('/sessions/')) return { group: 'Calls', title: 'Session' }
  if (path.startsWith('/agents/')) return { group: 'Configuration', title: 'Agent' }
  if (path.startsWith('/tools/')) return { group: 'Configuration', title: 'Tool' }
  if (path.startsWith('/webhooks/')) return { group: 'Configuration', title: 'Webhook' }
  // No `/transfers/:id` route — the list edits in place — so only the exact
  // path needs a crumb, and the GROUPS walk above already covers that.
  // Reached from the footer menu rather than from the nav, so GROUPS does not
  // know it and the loop above cannot find it. Without this line the breadcrumb
  // over somebody's own account page read "Not found".
  if (path === '/account') return { group: 'Access', title: 'Your account' }
  return { group: 'Voice AI', title: 'Not found' }
}

export { GROUPS as NAV_GROUPS }
